import React, { useState } from 'react';
import { styled } from '@glitz/react';
import Modal, { ModalScrollableContent, ModalTopContent } from 'Shared/SharedComponents/atoms/Modal';
import FacetViewModel from 'Shared/Facet/FacetViewModel.type';
import Accordion from 'Shared/Accordion';
import FilterSection from './FilterSection';
import * as style from 'Shared/Style';
import { ButtonTheme, SolidButton } from 'Shared/TextButtons';
import FacetValueViewModel from 'Shared/Facet/FacetValueViewModel.type';
import SwitchOption from './SwitchOption';
import { TextBlockTiny } from 'Shared/SharedComponents/atoms/Typography';
import ActiveFilters, { ActiveFilter as FilterItem, getActiveFacets } from '../ActiveFilters';

const FilterButtonsWrap = styled.div({
  width: '100%', 
  display: 'flex', 
  flexDirection: 'row', 
  justifyContent: 'space-between',
});

const FilterOnlyInStockWrap = styled.div({
  marginTop: '24px'
});

const FilterOnlyInStockItem = styled.div({
  marginTop: '16px'
});

const AccordionItem = styled(Accordion, {
  padding: {
    y: '16px'
  },
  margin: {
    y: 0,
  },
})

const ActiveFacets = styled(ActiveFilters, {
  margin: {
    top: '16px',
    bottom: '0'
  }
})

const ModalScrollableContentContainer = styled(ModalScrollableContent, {
  display: 'flex', 
  flexDirection: 'column', 
  alignItems: 'stretch'
})

const changeFilterValue = (
  filters: FacetViewModel[],
  changedFilter: FacetViewModel,
  filterValues: FacetValueViewModel[],
) => {
  return filters.map((filter) => {
    if (changedFilter.id === filter.id) {
      filter.values = filterValues;
    }

    return filter;
  });
};

const isActiveFilter = (filters: FacetViewModel[]): boolean => {
  if(filters === null || filters?.length < 1) {
    return false;
  }

  for (let filterIndex = 0; filterIndex < filters.length; filterIndex++) {
    if (
      (filters[filterIndex].minSelected && filters[filterIndex].minSelected !== filters[filterIndex].min) ||
      (filters[filterIndex].maxSelected && filters[filterIndex].maxSelected !== filters[filterIndex].max)
    ) {
      return true;
    }

    const values = filters[filterIndex].values;
    for (let valueIndex = 0; valueIndex < values.length; valueIndex++) {
      if (values[valueIndex].selected) {
        return true;
      }
    }
  }

  return false;
};

const ActiveFilterHeading = styled.div({
  margin: {top: '24px'},
  textTransform: 'uppercase',
  font: {
    weight: 500,
    size: '14px'
  }
})

type PropType = {
  modalIsShown: boolean;
  hideModal: () => void;
  heading: string;
  filters: FacetViewModel[];
  onFiltersUpdated: (filters: FacetViewModel[], ticket: string) => void;
  onFiltersReset: () => void;
  clearFiltersLabel: string;
  showProductsLabel: string;
  activeFiltersLabel?: string | boolean;
  removeActiveFilter?: () => void;
};

const FilterModal = ({
  modalIsShown,
  hideModal,
  heading,
  filters,
  onFiltersUpdated,
  onFiltersReset,
  clearFiltersLabel,
  showProductsLabel,
  activeFiltersLabel = false,
  removeActiveFilter
}: PropType) => {
  const [filterIdOpen, setFilterIdOpen] = useState('');
  
  const activeFacets = getActiveFacets(filters)

  const itemCss = {
    backgroundColor: '#ffffff',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.15)'
  }

  return (
    <Modal
      modalIsShown={modalIsShown}
      hideModal={hideModal}
      backgroundColor={style.colors.monochrome.extraLightGrey}
      isOnLeft
    >
      <ModalTopContent textAboveHeading="" bottomPadding={24} heading={heading} closeModal={hideModal} closeIconSize={24}>
      {(activeFacets?.length ?? 0) > 0 ? <>
        {activeFiltersLabel ? <ActiveFilterHeading>{activeFiltersLabel}</ActiveFilterHeading> : ''}
        <ActiveFacets itemCss={ itemCss } onRemoveFacet={removeActiveFilter} activeFacets={activeFacets} />
        </> : <></>}
      </ModalTopContent>
      <ModalScrollableContentContainer>
        {!!filters &&
          filters.map((filter, index) => {
            if(filter.id.toLowerCase() === "onlyinstock") {
              return (
                <FilterOnlyInStockWrap key={filter.id}>
                  <TextBlockTiny upperCase >
                    {filter.displayName}
                  </TextBlockTiny>
                  <FilterOnlyInStockItem>
                    <SwitchOption 
                      facet={filter}
                      onFilterValuesUpdated={(filterValues, ticket) => {
                        filters = changeFilterValue(filters, filter, filterValues);
                        onFiltersUpdated(filters, ticket);
                      }}
                    />
                  </FilterOnlyInStockItem> 
                </FilterOnlyInStockWrap>
              );
            }

            return (
              <AccordionItem
                key={filter.id}
                isOpen={filter.id === filterIdOpen}
                onToggle={() => {
                  setFilterIdOpen(filter.id === filterIdOpen ? '' : filter.id);
                }}
                upperCaseHeading
                disabled={(filter.values.length <= 0 && filter.type.toUpperCase() === 'TEXT') || (filter.type.toUpperCase() === "RANGE" && filter.min === filter.max)}
                header={filter.selectedCount > 0 && filter.id !== 'price' ? `${filter.displayName} (${filter.selectedCount})` : filter.displayName}
                borderWidth={2}
                iconWidth={'16px'}
              >
                <FilterSection
                  filter={filter}
                  subHeading={filter.id === 'price' ? filter.displayName : ''}
                  onFilterValuesUpdated={(filterValues, ticket) => {
                    filters = changeFilterValue(filters, filter, filterValues);
                    onFiltersUpdated(filters, ticket);
                  }}
                  onPriceUpdated={(changedFilter, ticket) => {
                    const changedFilters = filters.map((filter) => {
                      if (changedFilter.id === filter.id) {
                        return changedFilter;
                      }

                      return filter;
                    });
                    onFiltersUpdated(changedFilters, ticket);
                  }}
                />
              </AccordionItem>
            );
          })}
      </ModalScrollableContentContainer>
      <FilterButtonsWrap>
        <SolidButton
          text={clearFiltersLabel}
          onClick={() => {
            onFiltersReset();
            hideModal();
          }}
          disabled={!isActiveFilter(filters)}
          theme={ButtonTheme.TransparentBlack}
        />
        <SolidButton text={showProductsLabel} onClick={hideModal} theme={ButtonTheme.BlackToWhite} />
      </FilterButtonsWrap>
    </Modal>
  );
};

export default FilterModal;
